import type {PhotoRequirement} from './types';

/**
 * Does this element lay itself out — drawing its own frame and setting its own
 * width — so the shared surfaces must keep their hands off it?
 *
 * **One fact, two consequences.** It started as two one-line `type === 'table'`
 * predicates written where each was first needed — `drawsOwnChrome` in
 * `LivePreview`, `setsItsOwnWidth` in `ContextualToolbar` — which is how the
 * shared canvas and the shared toolbar each came to know what a table is. Five
 * branch points in the renderer alone is where "the canvas has a special case"
 * becomes "the canvas has a table mode".
 *
 * Briefly it was a two-field descriptor, which was worse: both fields were set
 * together by one producer and never disagreed, so it modelled a distinction
 * that does not exist. There is one question here, asked from two places:
 *
 * - **The canvas draws no frame and no resize handles, and does not clip the
 *   element to its box.** A table draws its own borders, highlights the cell
 *   under the pointer, and carries four corner handles, four edge strips and a
 *   move grip. The canvas outline landed a second purple rectangle around all of
 *   that, and the canvas resize handles landed a second set of squares on top of
 *   the table's own: eight handles in the same pixels, half of which scale the
 *   grid and half of which only stretch the box. An absolute table was unusable
 *   for that reason, while a flow table — which the canvas gives neither of —
 *   behaved exactly as intended. The clip goes with them, because a handle that
 *   straddles an edge lives outside the box.
 * - **The toolbar's flow-width fraction is inert, so it is not offered.**
 *   `flow_width` sets the WRAPPER's width; a table sets its own inside that
 *   wrapper (`tableWidth` in px, which every edge and corner drag writes), so
 *   the fraction was overridden the moment anyone resized the table. Two
 *   controls for one number, one of which silently lost.
 *
 * Deliberately a fact about the ELEMENT rather than a prop threaded down: a
 * caller cannot get it wrong if it is never asked. When something other than a
 * table needs this, it is one line here rather than a sixth predicate out there.
 */
export function laysItselfOut(item: Pick<PhotoRequirement, 'type'>): boolean {
  return item.type === 'table';
}
